"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
const faqs = [{
  question: "What is Macaroni Capital's core philosophy?",
  answer: "We believe in systematic trading, simplified. Our platform is designed to remove emotional decision-making from trading by providing structured, AI-assisted planning tools and comprehensive pattern recognition education."
}, {
  question: "Does Macaroni Capital provide investment advice?",
  answer: "No. Macaroni Capital is strictly an educational platform. We provide tools, frameworks, and educational content to help you build your own trading discipline. Nothing on this site constitutes investment advice."
}, {
  question: "How does the AI Caddie work?",
  answer: "The AI Caddie acts as your objective sounding board. It helps structure your weekly trading plans, reviews your setups against our 16-slot pattern library, and ensures you adhere to your predefined systematic rules."
}, {
  question: "When will the platform officially launch?",
  answer: "We are currently in a closed beta phase, refining our tools with a select group of professional traders. We anticipate opening early access to our waitlist members in the coming months."
}, {
  question: "Are there any account requirements to use the core tools?",
  answer: "Our foundational educational content and basic planning frameworks are accessible without an account. However, personalized AI Caddie features and saving your structured plans require a premium tier subscription."
}];
function WaitlistForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="mt-12 p-6 border border-[#B8922A]/30 bg-[#0A0E14]/50 text-center">
        <p className="font-serif text-[#B8922A] text-lg">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="mt-12 w-full max-w-2xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-stretch w-full">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1 w-full space-y-0 grid gap-0">
              <FormControl>
                <Input type="email" placeholder="Enter your email address" className="rounded-none border border-[#F4EFE6] bg-transparent text-[#F4EFE6] placeholder:text-[#F4EFE6]/50 h-14 px-6 font-serif text-lg focus-visible:ring-0 focus-visible:border-[#B8922A] transition-colors" {...field} />
              </FormControl>
              <div className="h-6 mt-2 text-left">
                <FormMessage className="text-red-400 font-sans text-xs tracking-wider uppercase" />
              </div>
            </FormItem>} />
        <Button type="submit" disabled={isSubmitting} className="rounded-none bg-[#B8922A] text-[#0A0E14] hover:bg-[#D4AE52] h-14 px-10 font-sans font-semibold tracking-widest uppercase sm:ml-[-1px] w-full sm:w-auto mt-4 sm:mt-0 transition-colors border border-[#B8922A]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Joining...
            </> : "Join Waitlist"}
        </Button>
      </div>
      
      {submitError && <p className="text-red-400 font-sans text-sm text-center mt-4 uppercase tracking-wider">
          {submitError}
        </p>}
      
      <p className="text-[#F4EFE6]/30 font-sans text-[11px] sm:text-[13px] uppercase tracking-[0.2em] text-center mt-6">
        Sign up for exclusive early access and updates.
      </p>
    </Form>;
}
export default function InvestorFaq() {
  return <section id="investor-faq" className="dark bg-background text-foreground">
      <div className="bg-[#141A22] w-full py-24 sm:py-32 px-6 sm:px-8 lg:px-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-sans text-3xl sm:text-4xl text-[#F4EFE6] tracking-wider uppercase mb-4">
              Investor FAQ
            </h2>
            <div className="w-16 h-[1px] bg-[#B8922A] mx-auto"></div>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-[#B8922A]/20">
                <AccordionTrigger className="[&_svg]:hidden hover:no-underline py-6 group">
                  <span className="font-sans text-[15px] text-[#F4EFE6] group-hover:text-[#B8922A] transition-colors text-left tracking-wide pr-8">
                    {faq.question}
                  </span>
                  <span className="text-[#B8922A] text-sm transition-transform duration-300 group-data-[state=open]:rotate-180 shrink-0">
                    ◆
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-8 pt-2">
                  <p className="font-serif text-[16px] text-[#F4EFE6]/80 leading-relaxed">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>

          <FormProvider formId="investor-waitlist-faq" sectionName="Investor FAQ" defaultValues={{
          email: ""
        }} submitText="Join Waitlist" successMessage="Thank you. Your position on the waitlist has been secured.">
            <WaitlistForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}